import {
  detectArgentinaProvinceFromTimezone,
  getArgentinaProvinceConfig,
  getTemplateForArgentinaProvince,
  type ArgentinaProvinceConfig
} from "@/config/argentinaProvinces";
import { detectArgentinaProvinceEnhanced } from "@/lib/argentinaProvinceDetection";
import { useEffect, useState } from "react";

/**
 * Hook to manage Argentina-specific template selection based on provinces
 * Similar to Spain's system but for Argentine provinces
 */
export const useArgentinaTemplate = () => {
  const [provinceCode, setProvinceCode] = useState<string | null>(null);
  const [provinceConfig, setProvinceConfig] = useState<ArgentinaProvinceConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const detectArgentinaProvince = async () => {
      if (typeof window === "undefined") {
        setIsLoading(false);
        return;
      }

      // First check if user is actually in Argentina
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const browserLang = navigator.language;
      const langCountry = browserLang.split("-")[1]?.toUpperCase();

      const isArgentineTimezone = timezone.includes("Argentina") || 
                                 timezone.includes("Buenos_Aires") || 
                                 timezone === "America/Argentina/Buenos_Aires" ||
                                 timezone === "America/Argentina/Cordoba" ||
                                 timezone === "America/Argentina/Mendoza";
      
      const isArgentineLanguage = langCountry === "AR";
      
      // Only run province detection if clearly in Argentina
      if (!isArgentineTimezone && !isArgentineLanguage) {
        setIsLoading(false);
        return;
      }

      try {
        // Check for test province first (development)
        const testProvince = localStorage.getItem("test_argentina_province");
        if (testProvince && import.meta.env.DEV) {
          const config = getArgentinaProvinceConfig(testProvince);
          if (isMounted) {
            setProvinceCode(testProvince);
            setProvinceConfig(config);
            setIsLoading(false);
          }
          return;
        }

        // Try enhanced detection first
        const enhancedProvince = await detectArgentinaProvinceEnhanced();
        
        if (enhancedProvince && isMounted) {
          const config = getArgentinaProvinceConfig(enhancedProvince);
          setProvinceCode(enhancedProvince);
          setProvinceConfig(config);
          if (import.meta.env.DEV) {
            console.log("Argentina province config", {
              provinceName: config?.provinceName,
              template: config?.template,
              region: config?.region,
            });
          }
          if (isMounted) {
            setIsLoading(false);
          }
          return;
        }

        // Fallback to timezone detection
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const detectedProvince = detectArgentinaProvinceFromTimezone(timezone);
        
        if (detectedProvince && isMounted) {
          const config = getArgentinaProvinceConfig(detectedProvince);
          setProvinceCode(detectedProvince);
          setProvinceConfig(config);
          if (import.meta.env.DEV) {
            console.log("Argentina province (timezone)", {
              timezone,
              provinceName: config?.provinceName,
              template: config?.template,
              region: config?.region,
            });
          }
        } else if (isMounted) {
          // Check if it's Argentina but we can't detect specific province
          if (timezone.includes("Argentina")) {
            // Set a default province code for Argentina
            setProvinceCode("AR_UNKNOWN");
            setProvinceConfig(null);
          }
        }
      } catch (error) {
        if (isMounted) {
          // Fallback to Buenos Aires
          setProvinceCode("BA");
          setProvinceConfig(getArgentinaProvinceConfig("BA"));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    detectArgentinaProvince();

    return () => {
      isMounted = false;
    };
  }, []);

  const getTemplateForCurrentProvince = (): "BaseTemplate" | "BioLinkTemplate" => {
    if (provinceCode && provinceCode !== "AR_UNKNOWN") {
      return getTemplateForArgentinaProvince(provinceCode);
    }
    // Default to BaseTemplate for unknown provinces (more accessible)
    return "BaseTemplate";
  };

  const shouldUseBaseTemplate = (): boolean => {
    return getTemplateForCurrentProvince() === "BaseTemplate";
  };

  const shouldUseBioLinkTemplate = (): boolean => {
    return getTemplateForCurrentProvince() === "BioLinkTemplate";
  };

  const isHighIncomeProvince = (): boolean => {
    return provinceConfig?.economicLevel === "high" || false;
  };

  const getProvinceRegion = (): string | null => {
    return provinceConfig?.region || null;
  };

  const getProvinceName = (): string | null => {
    return provinceConfig?.provinceName || null;
  };

  // Development helper functions
  const setTestProvince = (testProvinceCode: string) => {
    if (import.meta.env.DEV) {
      localStorage.setItem("test_argentina_province", testProvinceCode);
      window.location.reload(); // Reload to apply changes
    }
  };

  const clearTestProvince = () => {
    if (import.meta.env.DEV) {
      localStorage.removeItem("test_argentina_province");
      window.location.reload(); // Reload to apply changes
    }
  };

  return {
    // Province data
    provinceCode,
    provinceConfig,
    isLoading,
    
    // Template selection
    templateName: getTemplateForCurrentProvince(),
    shouldUseBaseTemplate,
    shouldUseBioLinkTemplate,
    
    // Province information
    isHighIncomeProvince,
    getProvinceRegion,
    getProvinceName,
    
    // Development helpers
    setTestProvince,
    clearTestProvince,
  };
};